/**
 * A calendar date with no time and no zone.
 *
 * Authorizations, rates and service dates are **calendar** facts. An
 * authorization that runs to 31 March runs to 31 March in the client's town,
 * not to an instant that happens to be 31 March in UTC and 30 March in Denver.
 * Modelling them as `Date` is how a visit on the last day of a period gets
 * counted in the next one for every agency west of Greenwich — and the symptom
 * is a denied claim months later, which is R-1b.
 *
 * So the representation is the ISO string itself, branded so it cannot be
 * confused with an arbitrary string, and every operation here is integer
 * arithmetic on year/month/day.
 *
 * ## Why `Date.UTC` appears below and is not a contradiction
 *
 * Converting to a day number and back is the least error-prone way to add days
 * across month and year boundaries, and `Date.UTC` is a pure function from
 * integers to an integer. It is used only as arithmetic: no local getters, no
 * `new Date()`, no clock, no zone. The value never escapes as a `Date`.
 */
export class InvalidLocalDateError extends Error {
    value;
    constructor(value) {
        super(`"${value}" is not a calendar date. Expected YYYY-MM-DD with a real day — ` +
            'note that 2023-02-29 is rejected and 2024-02-29 is not.');
        this.value = value;
        this.name = 'InvalidLocalDateError';
    }
}
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** Days in `month` (1-12) of `year`, Gregorian. */
export function daysInMonth(year, month) {
    // February is the only month that moves, so the leap rule is the only rule.
    if (month === 2)
        return isLeapYear(year) ? 29 : 28;
    return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31;
}
export function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}
/**
 * Parse and validate. Rejects a well-formed string that is not a real date —
 * `2023-02-29` and `2024-04-31` both throw, which is the whole reason this
 * exists rather than a cast.
 */
export function toLocalDate(value) {
    const match = ISO_DATE.exec(value);
    if (!match)
        throw new InvalidLocalDateError(value);
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12)
        throw new InvalidLocalDateError(value);
    if (day < 1 || day > daysInMonth(year, month))
        throw new InvalidLocalDateError(value);
    return value;
}
/** The year, month (1-12) and day of a date, as numbers. */
export function parts(date) {
    // Already validated by construction, so this cannot fail.
    return {
        year: Number(date.slice(0, 4)),
        month: Number(date.slice(5, 7)),
        day: Number(date.slice(8, 10)),
    };
}
const pad = (n, width) => String(n).padStart(width, '0');
/** Build a date from components. Throws on a day the month does not have. */
export function fromParts(year, month, day) {
    return toLocalDate(`${pad(year, 4)}-${pad(month, 2)}-${pad(day, 2)}`);
}
/** Days since the Unix epoch. Pure integer arithmetic; see the note above. */
export function toEpochDay(date) {
    const { year, month, day } = parts(date);
    return Date.UTC(year, month - 1, day) / 86_400_000;
}
export function fromEpochDay(epochDay) {
    const ms = epochDay * 86_400_000;
    const year = new Date(ms).getUTCFullYear();
    const month = new Date(ms).getUTCMonth() + 1;
    const day = new Date(ms).getUTCDate();
    return fromParts(year, month, day);
}
export function addDays(date, days) {
    return fromEpochDay(toEpochDay(date) + days);
}
/**
 * Add months, clamping the day to the target month's length.
 *
 * 31 January plus one month is 28 February, or 29 February in a leap year.
 * There is no correct answer here, only a consistent one — and clamping is what
 * every payer means by "the same date next month", because the alternative
 * (rolling into March) would make a monthly period skip a day.
 */
export function addMonths(date, months) {
    const { year, month, day } = parts(date);
    const zeroBased = year * 12 + (month - 1) + months;
    const targetYear = Math.floor(zeroBased / 12);
    const targetMonth = (zeroBased % 12) + 1;
    return fromParts(targetYear, targetMonth, Math.min(day, daysInMonth(targetYear, targetMonth)));
}
/** 0 = Sunday … 6 = Saturday. */
export function dayOfWeek(date) {
    // The epoch, 1970-01-01, was a Thursday — day 4 — and the modulo keeps the
    // result non-negative for dates before it.
    return (((toEpochDay(date) + 4) % 7) + 7) % 7;
}
/** Negative if `a` is earlier, 0 if equal, positive if later. */
export function compareLocalDate(a, b) {
    // ISO-8601 dates of equal width sort lexicographically, which is the one
    // genuinely nice property of the format.
    return a < b ? -1 : a > b ? 1 : 0;
}
export const isBefore = (a, b) => a < b;
export const isAfter = (a, b) => a > b;
/** Inclusive on both ends. */
export function isWithin(date, range) {
    return date >= range.start && date <= range.end;
}
//# sourceMappingURL=local-date.js.map